import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { useStyles, ROW_H, LABEL_COL_W, HEADER_H } from './Styles';
import {
  ROW_KIND,
  STATUS,
  STATUS_COLOURS,
  ZOOM,
  barColourOf,
  barGeometry,
  dateWindowOf,
  elapsedFractionOf,
  flattenVisible,
  headerBandsOf,
  inclusiveDays,
  longDate,
  scaleOf,
  shortDate,
  todayXOf,
} from './ganttModel';

/**
 * The Gantt grid itself — timeline header, grid lines, rows, bars, today marker
 * and the row tooltip (Gantt Chart FSD F–K).
 *
 * Layout: the row-label column is pinned left (`position: sticky`) while the
 * timeline scrolls horizontally beneath a sticky two-tier header, per FSD H
 * ("The row label column ... remains pinned to the left of the screen while the
 * timeline scrolls horizontally underneath the sticky header").
 *
 * Presentation only — every rule about what a row IS (status, rollup, span,
 * colour, elapsed fraction, which ticks the header carries) lives in
 * ./ganttModel. This component decides where pixels go and nothing else.
 */

// Bars wider than this show their date range as inline text (FSD F: "showing
// the short date range as inline text once the bar is wide enough").
//
// The FSD quotes 64px, which is a prototype measurement and too tight for the
// label actually rendered here: "20 Mar – 06 Apr" is ~76px at this font size,
// so at 64px the text was visibly cut off mid-word inside the bar. Raised to
// clear the widest form of the label plus its padding — a clipped date reads as
// a rendering bug, and the exact dates are in the tooltip either way.
const INLINE_TEXT_MIN_W = 96;

const isSummary = (kind) => kind !== ROW_KIND.BATCH;

// Text colour that stays legible on the bar's own fill. The batch-level matrix
// colours are pale, so they take dark text; every deeper level takes white.
const barTextColourOf = (kind) => (kind === ROW_KIND.BATCH ? '#1B1B1B' : '#FFFFFF');

const GanttGrid = ({
  rows,
  zoom,
  expanded,
  onToggleExpand,
  onDrillCampaign,
  campaignRow,
  showProgress,
  today,
}) => {
  const classes = useStyles();

  // Available width drives the Portfolio+Quarter auto-fit (BR-07). Measured
  // from the scroll container minus the pinned label column.
  //
  // The same pass sizes the box vertically: it fills from its own top down to
  // the bottom of the window, so the chart (not the page) owns the vertical
  // scroll and the sticky timeline header stays in view however many rows are
  // expanded. maxHeight, not height, so a short chart still collapses to its
  // content instead of leaving a tall empty box.
  const scrollRef = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [maxHeight, setMaxHeight] = useState(null);
  useLayoutEffect(() => {
    const measure = () => {
      const el = scrollRef.current;
      if (!el) return;
      setAvailableWidth(Math.max(0, el.clientWidth - LABEL_COL_W));
      // Leaves room for the legend strip and the page's bottom padding.
      const top = el.getBoundingClientRect().top;
      setMaxHeight(Math.max(320, Math.round(window.innerHeight - top - 76)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const visible = useMemo(() => flattenVisible(rows, expanded), [rows, expanded]);

  const window_ = useMemo(() => dateWindowOf(rows, { campaignRow }), [rows, campaignRow]);

  // Auto-fit applies at Portfolio view + Quarter zoom only; everything else
  // uses the fixed pixels-per-day scale and scrolls (BR-07).
  const autoFit = !campaignRow && zoom === ZOOM.QUARTER;
  const scale = useMemo(
    () => (window_ ? scaleOf(window_, zoom, availableWidth, { autoFit }) : null),
    [window_, zoom, availableWidth, autoFit]
  );

  const bands = useMemo(
    () => (window_ && scale ? headerBandsOf(window_, zoom, scale) : { major: [], minor: [] }),
    [window_, scale, zoom]
  );

  const todayX = useMemo(
    () => (window_ && scale ? todayXOf(window_, scale, today) : null),
    [window_, scale, today]
  );

  // Hover tooltip. Closed on mouse-leave and on any state change — zoom,
  // filter, expand/collapse (FSD C, E and J all require this).
  const [tip, setTip] = useState(null);
  React.useEffect(() => { setTip(null); }, [zoom, expanded, rows, campaignRow]);

  // Measured tooltip box, so the flip/clamp below works off its real size
  // rather than a guess (its height varies — the FSD's body rows are rendered
  // only where the underlying value exists). Seeded with a reasonable estimate
  // for the very first frame, then corrected once mounted.
  const tipRef = useRef(null);
  const [tipSize, setTipSize] = useState({ w: 220, h: 120 });
  React.useLayoutEffect(() => {
    if (!tip || !tipRef.current) return;
    const { offsetWidth: w, offsetHeight: h } = tipRef.current;
    setTipSize((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
  }, [tip]);

  // Order matters. An empty `rows` means the status filters removed everything
  // (the parent already handles "the portfolio itself is empty" before
  // rendering this component), and an empty row set ALSO makes the date window
  // null — so the window guard has to come second or it would answer a
  // filtering question with a data message. FSD D is explicit about the
  // wording: "If a filter combination leaves zero visible rows, the chart body
  // shows 'No rows match the current status filters.'".
  if (!rows.length || !visible.length) {
    return <div className={classes.state}>No rows match the current status filters.</div>;
  }
  if (!window_ || !scale) {
    return <div className={classes.state}>No dated activity to plot for this selection.</div>;
  }

  const laneWidth = scale.width;
  const colHeader = campaignRow ? 'STAGE / BATCH' : 'PROJECT / CAMPAIGN / STAGE / BATCH';

  // Anchored to the bar's left edge. `geo.left` is measured inside the LANE,
  // but the tooltip is a child of the row body, whose origin is the left edge
  // of the pinned label column — so the label width has to be added back or the
  // tooltip lands a whole column to the left of the bar it describes.
  const openTip = (row, geo, rowIndex) => () => {
    // The chart scrolls on both axes, so the tooltip has to be placed against
    // what is ON SCREEN, not against the full content box — anchored to the
    // content alone it lands below the fold whenever the hovered row sits near
    // the bottom edge. Captured at hover time; if the user then scrolls, the
    // tooltip travels with its row, which is what you want anyway.
    const el = scrollRef.current;
    const view = el
      ? {
          top: el.scrollTop,
          left: el.scrollLeft,
          height: el.clientHeight,
          width: el.clientWidth,
        }
      : null;
    setTip({ row, laneLeft: geo.left, rowIndex, view });
  };

  const statusPill = (status) => {
    const c = STATUS_COLOURS[status] || STATUS_COLOURS[STATUS.PLANNED];
    return (
      <span className={classes.rowStatusPill} style={{ background: `${c.fill}1A`, color: c.text }}>
        {status}
      </span>
    );
  };

  const renderTooltip = () => {
    if (!tip) return null;
    const r = tip.row;
    const c = STATUS_COLOURS[r.status] || STATUS_COLOURS[STATUS.PLANNED];
    const elapsed = elapsedFractionOf(r, today);
    // FSD J: body rows shown, in order, ONLY when the underlying value is
    // present — so each entry is conditional rather than rendered as a dash.
    const lines = [
      r.start && ['Start', longDate(r.start)],
      r.end && ['End', longDate(r.end)],
      r.start && r.end && ['Duration', `${inclusiveDays(r.start, r.end)} days`],
      elapsed != null && ['Elapsed', `${Math.round(elapsed * 100)}%`],
      r.plannedStart && ['Planned start', longDate(r.plannedStart)],
      r.plannedEnd && ['Planned end', longDate(r.plannedEnd)],
      r.yield && r.yield.target != null && ['Target yield', `${r.yield.target} ${r.yield.uom}`],
      r.yield && r.yield.actual != null && ['Actual yield', `${r.yield.actual} ${r.yield.uom}`],
    ].filter(Boolean);

    // Placement (FSD J: "flips above or below the row depending on the row's
    // vertical position"). The tooltip is a child of the row body, so its
    // coordinates are content coordinates; the visible band is that content
    // window minus the sticky header and the pinned label column, both of which
    // overlay it. Preferred position is just below the row, flipping above when
    // that would fall out of view, and clamped into the band as a last resort.
    // It is pointer-events: none, so overlaying a row costs nothing.
    const bodyH = visible.length * ROW_H;
    const view = tip.view;
    const visTop = view ? view.top : 0;
    const visBottom = view ? view.top + view.height - HEADER_H : bodyH;

    const rowTop = tip.rowIndex * ROW_H;
    const below = rowTop + ROW_H + 4;
    const above = rowTop - tipSize.h - 4;
    let top;
    if (below + tipSize.h <= visBottom) top = below;
    else if (above >= visTop) top = above;
    else top = Math.max(visTop, Math.min(below, visBottom - tipSize.h));

    // Horizontal: inside the visible timeline, clear of the pinned label column
    // (FSD J: "positioned to stay within the chart's content width").
    const minLeft = (view ? view.left : 0) + LABEL_COL_W + 8;
    const maxLeft = view
      ? view.left + view.width - tipSize.w - 8
      : LABEL_COL_W + laneWidth - tipSize.w - 8;
    const left = Math.max(minLeft, Math.min(LABEL_COL_W + tip.laneLeft, maxLeft));

    return (
      <div ref={tipRef} className={classes.tooltip} style={{ left, top }}>
        <div className={classes.tipHead}>
          <span className={classes.tipTitle}>{r.name}</span>
          <span className={classes.rowStatusPill} style={{ background: `${c.fill}1A`, color: c.text }}>
            {r.status}
          </span>
        </div>
        {lines.map(([k, v]) => (
          <div className={classes.tipRow} key={k}>
            <span className={classes.tipKey}>{k}</span>
            <span className={classes.tipVal}>{v}</span>
          </div>
        ))}
        {r.kind === ROW_KIND.CAMPAIGN && (
          <div className={classes.tipDrill}>Click bar to drill into Campaign →</div>
        )}
      </div>
    );
  };

  return (
    <div className={classes.chartBox}>
      <div
        className={classes.chartScroll}
        ref={scrollRef}
        style={maxHeight ? { maxHeight } : undefined}
      >
        <div className={classes.chartInner} style={{ width: LABEL_COL_W + laneWidth }}>
          {/* ── two-tier sticky timeline header (FSD H) ── */}
          <div className={classes.headerRow}>
            <div className={classes.labelHeader}>{colHeader}</div>
            <div className={classes.bandsWrap} style={{ width: laneWidth }}>
              {bands.major.map((t) => (
                <div key={`M${t.key}`} className={classes.bandMajor} style={{ left: t.left, width: t.width }}>
                  {t.label}
                </div>
              ))}
              {bands.minor.map((t) => (
                <div key={`m${t.key}`} className={classes.bandMinor} style={{ left: t.left, width: t.width }}>
                  {t.label}
                </div>
              ))}
              {/* Today pill anchored at the top of the header (FSD H). */}
              {todayX != null && (
                <div className={classes.todayPill} style={{ left: todayX }}>Today</div>
              )}
            </div>
          </div>

          {/* ── rows ── */}
          <div className={classes.body}>
            {visible.map((row, i) => {
              // Deeper levels get a lighter tint; the Project rule is skipped on
              // the very first row so it does not double up with the header's
              // own bottom border.
              const levelClass = {
                [ROW_KIND.PROJECT]: classes.rowProject,
                [ROW_KIND.CAMPAIGN]: classes.rowCampaign,
                [ROW_KIND.STAGE]: classes.rowStage,
              }[row.kind] || '';
              const geo = barGeometry(row, window_, scale);
              const colour = barColourOf(row.kind, row.status);
              const hasChildren = (row.children || []).length > 0;
              const open = expanded.has(row.id);
              const drillable = row.kind === ROW_KIND.CAMPAIGN;
              const elapsed = showProgress ? elapsedFractionOf(row, today) : null;
              const showInlineText = geo && geo.width > INLINE_TEXT_MIN_W && row.start && row.end;

              return (
                <div
                  className={`${classes.row} ${levelClass}`}
                  key={row.id}
                  style={i === 0 ? { borderTop: 'none' } : undefined}
                >
                  {/* Pinned label column */}
                  <div className={classes.labelCell} style={{ paddingLeft: 16 + row.depth * 20 }}>
                    {hasChildren ? (
                      <button
                        type="button"
                        className={classes.twisty}
                        onClick={() => onToggleExpand(row.id)}
                        aria-label={open ? `Collapse ${row.name}` : `Expand ${row.name}`}
                      >
                        {open ? <ExpandMoreIcon style={{ fontSize: 18 }} /> : <ChevronRightIcon style={{ fontSize: 18 }} />}
                      </button>
                    ) : (
                      <span className={classes.twistySpacer} />
                    )}
                    <span className={classes.labelStack}>
                      <span className={classes.labelLine}>
                        <span
                          className={[
                            classes.rowName,
                            row.kind === ROW_KIND.PROJECT ? classes.rowNameProject : '',
                            row.kind === ROW_KIND.CAMPAIGN ? classes.rowNameCampaign : '',
                            drillable ? classes.rowNameDrillable : '',
                          ].filter(Boolean).join(' ')}
                          onClick={drillable ? () => onDrillCampaign(row) : undefined}
                          title={row.name}
                        >
                          {row.name}
                        </span>
                        {statusPill(row.status)}
                      </span>
                      {row.sublabel && (
                        <span className={classes.rowSub} title={row.sublabel}>{row.sublabel}</span>
                      )}
                    </span>
                  </div>

                  {/* Timeline lane */}
                  <div className={classes.lane} style={{ width: laneWidth }}>
                    {/* Grid lines behind the bars at every tick boundary (FSD H). */}
                    {bands.minor.map((t) => (
                      <div key={`gm${t.key}`} className={classes.gridLineMinor} style={{ left: t.left }} />
                    ))}
                    {bands.major.map((t) => (
                      <div key={`gM${t.key}`} className={classes.gridLineMajor} style={{ left: t.left }} />
                    ))}
                    {todayX != null && <div className={classes.todayLine} style={{ left: todayX }} />}

                    {geo && (isSummary(row.kind) ? (
                      /* Project / Campaign / Stage — slim bracket bar with end
                         caps, rather than a full block (FSD F Behavior). */
                      <div
                        className={classes.summaryBar}
                        style={{ left: geo.left, width: geo.width, cursor: drillable ? 'pointer' : 'default' }}
                        onMouseEnter={openTip(row, geo, i)}
                        onMouseLeave={() => setTip(null)}
                        onClick={drillable ? () => onDrillCampaign(row) : undefined}
                      >
                        <span className={classes.summarySpine} style={{ background: colour }} />
                        <span className={classes.summaryCap} style={{ left: 0, background: colour }} />
                        <span className={classes.summaryCap} style={{ right: 0, background: colour }} />
                      </div>
                    ) : (
                      /* Batch — full rounded block bar. */
                      <div
                        className={classes.bar}
                        style={{ left: geo.left, width: geo.width, background: colour }}
                        onMouseEnter={openTip(row, geo, i)}
                        onMouseLeave={() => setTip(null)}
                      >
                        {elapsed != null && (
                          <span className={classes.elapsed} style={{ width: `${elapsed * 100}%` }} />
                        )}
                        {showInlineText && (
                          <span className={classes.barText} style={{ color: barTextColourOf(row.kind) }}>
                            {`${shortDate(row.start)} – ${shortDate(row.end)}`}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {renderTooltip()}
          </div>
        </div>
      </div>
    </div>
  );
};

GanttGrid.propTypes = {
  // Row tree from buildRowTree (already status-filtered by the parent).
  rows: PropTypes.array.isRequired,
  zoom: PropTypes.string.isRequired,
  // Set of expanded row ids.
  expanded: PropTypes.object.isRequired,
  onToggleExpand: PropTypes.func.isRequired,
  onDrillCampaign: PropTypes.func.isRequired,
  // The campaign being drilled into, or null at Portfolio view. Drives both the
  // date window and the label-column header.
  campaignRow: PropTypes.object,
  // FSD I / BR-09: the elapsed fill is suppressed entirely when off.
  showProgress: PropTypes.bool,
  today: PropTypes.instanceOf(Date),
};

GanttGrid.defaultProps = {
  campaignRow: null,
  showProgress: true,
  today: undefined,
};

export default GanttGrid;
